import type { AdminTransaction } from '@nestjs-boilerplate/types';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { type DataCardConfig, DataCardList } from '@/components/ui/data-card-list';
import { DataTable, sortableHeader } from '@/components/ui/data-table';
import { DateFilterInput } from '@/components/ui/date-filter-input';
import { Label } from '@/components/ui/label';
import { MobileSortControl, type MobileSortOption } from '@/components/ui/mobile-sort-control';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/features/dashboard/components/pagination';
import { SearchInput } from '@/features/dashboard/components/search-input';
import { formatPence } from '@/features/payments/api/balance';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, type PaginationSearch } from '@/lib/pagination-search';
import { type TransactionFilters, useTransactions } from '../api/transactions';

/** Sentinel for "no filter", since a Select cannot hold an empty value. */
const ANY = 'any';

const SOURCES = [
  { value: ANY, label: 'All sources' },
  { value: 'balance', label: 'Balance movements' },
  { value: 'payment', label: 'Card payments' },
];

/**
 * Types the filter offers.
 *
 * No "Any" entry, unlike SOURCES above: an empty selection is the no-filter
 * state for a multi-select, and a checkbox meaning "ignore the other checkboxes"
 * would be a control that contradicts itself.
 */
const TYPES = [
  { value: 'DEDUCTION', label: 'Deduction' },
  { value: 'CREDIT', label: 'Credit' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'SUCCEEDED', label: 'Payment succeeded' },
  { value: 'PENDING', label: 'Payment pending' },
  { value: 'FAILED', label: 'Payment failed' },
];

/**
 * What the server orders by when the request names no column.
 *
 * Held here so the When header shows the ordering that is actually in effect.
 * It is deliberately not sent: an explicit `sortBy=occurredAt` would make the
 * URL claim a choice the reader never made, and the server's unsorted order is
 * not quite the same thing — its tiebreaker runs descending too.
 */
const DEFAULT_SORTING: SortingState = [{ id: 'occurredAt', desc: true }];

/**
 * Sort options offered on mobile.
 *
 * The same three columns the table exposes a sortable header for — the ids are
 * the API's own sort columns and are sent as-is, so anything else here would be
 * a control that appears to work and returns a 400.
 *
 * Amount is signed, so descending really is largest credit to largest
 * deduction. Type is selected as text in the union, so it orders
 * alphabetically rather than by any enum's declaration order.
 */
const MOBILE_SORT_OPTIONS: MobileSortOption[] = [
  { id: 'occurredAt', label: 'When', descLabel: 'Newest first', ascLabel: 'Oldest first' },
  { id: 'amountPence', label: 'Amount', descLabel: 'Highest first', ascLabel: 'Lowest first' },
  { id: 'type', label: 'Type', descLabel: 'Z → A', ascLabel: 'A → Z' },
];

/**
 * The Amount header's control, built once rather than per render.
 *
 * Pulled out of the column so the right-alignment wrapper can sit around it —
 * `sortableHeader` returns a component, and calling it inline would make a new
 * component type on every render.
 */
const AmountHeader = sortableHeader<AdminTransaction>('Amount');

/**
 * Renders a signed amount.
 *
 * `formatPence` puts the minus inside the symbol ("£-15.00"), so the sign is
 * pulled out here to read as money rather than as arithmetic. Money leaving an
 * account is coloured, money arriving is not: an administrator scanning the
 * column is looking for spend.
 */
function Amount({ pence }: { pence: number }) {
  const outgoing = pence < 0;
  return (
    <span className={outgoing ? 'text-destructive tabular-nums' : 'tabular-nums'}>
      {outgoing ? '−' : ''}
      {formatPence(Math.abs(pence))}
    </span>
  );
}

/**
 * The transaction's type, with a payment's status beside it.
 *
 * Shared by the table cell and the card field so the two layouts cannot drift
 * apart on which rows get a filled badge and which get an outlined one.
 */
function TypeBadge({ row }: { row: AdminTransaction }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {/* A plain span rather than a new shadcn primitive: the section has no
          Badge yet, and pulling one in rewrites shared components that other
          screens already depend on. */}
      <span
        className={
          row.source === 'payment'
            ? 'rounded-md bg-secondary px-2 py-0.5 text-xs font-medium'
            : 'rounded-md border px-2 py-0.5 text-xs font-medium'
        }
      >
        {row.type}
      </span>
      {/* Only payments carry a status; balance rows already happened. */}
      {row.status ? (
        <span
          className={
            row.status === 'FAILED' ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'
          }
        >
          {row.status}
        </span>
      ) : null}
    </div>
  );
}

interface TransactionsPageProps {
  filters: TransactionFilters & PaginationSearch;
  onFiltersChange: (next: TransactionFilters & PaginationSearch) => void;
}

/**
 * Combined financial history across balance movements and card payments.
 *
 * Read-only. Nothing on this screen can change a figure; crediting a company is
 * a separate capability behind its own confirmation.
 *
 * Filter state is owned by the route and passed in, so this component needs no
 * router to be tested and the URL stays the single source of truth — a filtered
 * list survives navigating away and back, which it did not while the filters
 * lived in component state.
 */
export function TransactionsPage({ filters, onFiltersChange }: TransactionsPageProps) {
  const isMobile = useIsMobile();
  // Paging travels in the same search params as the filters, but the query hook
  // takes it as its own two arguments. Split back out here rather than left in
  // the filter object, which would send `page` twice in the query string.
  const { page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE, ...listFilters } = filters;

  const { data, isLoading, isError, error } = useTransactions(listFilters, page, pageSize);

  const items = data?.items ?? [];

  const update = useCallback(
    (patch: Partial<TransactionFilters & PaginationSearch>) => {
      // Any change to the filter set invalidates the current page number: page 7
      // of a set that now has two would show an empty table and read as "no
      // matches". The reset rides along in the one write rather than being a
      // second navigation, which would make one Back press appear to do nothing.
      const resetsPage = !('page' in patch);
      onFiltersChange({ ...filters, ...patch, ...(resetsPage ? { page: undefined } : {}) });
    },
    [filters, onFiltersChange],
  );

  // Falls back to the server's own default rather than to no sort at all, so
  // the When header does not show as unsorted while the rows are in fact
  // ordered by it.
  const sorting: SortingState = filters.sortBy
    ? [{ id: filters.sortBy, desc: filters.sortOrder !== 'asc' }]
    : DEFAULT_SORTING;

  const onSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      update(
        first
          ? {
              // Cast because TanStack reports a column id as a bare string. The
              // ids are declared below and are the API's own sort columns, and
              // the route's `validateSearch` narrows this again on the way back
              // in.
              sortBy: first.id as TransactionFilters['sortBy'],
              sortOrder: first.desc ? 'desc' : 'asc',
            }
          : { sortBy: undefined, sortOrder: undefined },
      );
    },
    [sorting, update],
  );

  // The per-column classes the hand-rolled table put on its TableHead/TableCell
  // live on a wrapper span here: DataTable renders those elements itself and
  // takes no className for them.
  //
  // Only the three columns the API can sort by carry a control. User, Company
  // and Description are joined or free text with no server-side ordering
  // behind them, and a header that looks like a control but reorders nothing is
  // worse than a plain one.
  const columns = useMemo<ColumnDef<AdminTransaction>[]>(
    () => [
      {
        // Named for the API's sort column, not the label: TanStack reports the
        // column id as the sort key and it is sent to the server as-is.
        id: 'occurredAt',
        header: sortableHeader<AdminTransaction>('When'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {new Date(row.original.occurredAt).toLocaleString()}
          </span>
        ),
      },
      {
        id: 'user',
        header: 'User',
        enableSorting: false,
        cell: ({ row }) => <span className="text-sm">{row.original.userEmail}</span>,
      },
      {
        id: 'company',
        header: 'Company',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.companyName ?? <span className="text-muted-foreground">—</span>}
          </span>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) => <span className="text-sm">{row.original.description}</span>,
      },
      {
        id: 'type',
        header: sortableHeader<AdminTransaction>('Type'),
        cell: ({ row }) => <TypeBadge row={row.original} />,
      },
      {
        id: 'amountPence',
        // The header is a button now, so the right-alignment that used to be
        // `text-right` on a block becomes a flex wrapper around it: text
        // alignment does not move a button within its cell.
        header: (context) => (
          <div className="flex justify-end whitespace-nowrap">{AmountHeader(context)}</div>
        ),
        cell: ({ row }) => (
          <div className="text-right">
            <Amount pence={row.original.amountPence} />
          </div>
        ),
      },
    ],
    [],
  );

  // The same six columns as cards. What the amount is *for* names the card, and
  // the amount itself is the trailing element: on a phone the figure is what is
  // being scanned for, so it sits top-right where the eye lands rather than
  // last in a list of six labelled fields. The remaining four stay as fields,
  // ordered by how often they answer the question the reader arrived with.
  const cardConfig = useMemo<DataCardConfig<AdminTransaction>>(
    () => ({
      header: (row) => <span className="font-medium">{row.description}</span>,
      // No `text-right`: DataCard already pins the trailing slot to the right,
      // and the wrapper is sized to its content.
      trailing: (row) => <Amount pence={row.amountPence} />,
      fields: [
        { label: 'When', render: (row) => new Date(row.occurredAt).toLocaleString() },
        { label: 'User', render: (row) => row.userEmail },
        {
          label: 'Company',
          render: (row) => row.companyName ?? <span className="text-muted-foreground">—</span>,
        },
        { label: 'Type', render: (row) => <TypeBadge row={row} /> },
      ],
    }),
    [],
  );

  // Bound once and rendered by both layouts: the net and the pager say the same
  // thing on a phone as on a desktop, and duplicating them is how the two drift.
  const summary =
    items.length > 0 ? (
      <div className="space-y-3 border-t p-4">
        <p className="text-sm text-muted-foreground">
          Net balance movement across all {data?.total ?? 0} matching rows:{' '}
          <Amount pence={data?.netPence ?? 0} />
          {/* Card payments are excluded from the net: a top-up appears twice,
              as the charge and as the credit it produced. */}
          <span className="ml-1">(card payments excluded to avoid double-counting)</span>
        </p>
        <Pagination
          page={data?.page ?? 1}
          pageSize={data?.pageSize ?? pageSize}
          total={data?.total ?? 0}
          totalPages={data?.totalPages ?? 0}
          onPageChange={(next) => update({ page: next })}
          onPageSizeChange={(next) => update({ pageSize: next, page: undefined })}
        />
      </div>
    ) : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Transactions</h1>
      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="user">User</Label>
            {/* Debounced, so a typed address is one request and one URL write
                rather than one of each per keystroke. */}
            <SearchInput
              id="user"
              placeholder="Email"
              value={filters.user ?? ''}
              onChange={(user) => update({ user: user || undefined })}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="source">Source</Label>
            <Select
              value={filters.source ?? ANY}
              onValueChange={(value) =>
                update({
                  source: value === ANY ? undefined : (value as TransactionFilters['source']),
                })
              }
            >
              <SelectTrigger id="source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <MultiSelect
              id="type"
              value={filters.type ?? []}
              onChange={(value) => update({ type: value.length > 0 ? value : undefined })}
              options={TYPES}
              placeholder="Any type"
            />
          </div>
          {/* Both ends inclusive. The pair is cross-bounded, so the calendar
              cannot offer a range that reads backwards and returns nothing. */}
          <div className="space-y-2">
            <Label htmlFor="from">From</Label>
            <DateFilterInput
              id="from"
              value={filters.from}
              onChange={(from) => update({ from })}
              max={filters.to ? new Date(filters.to) : undefined}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="to">To</Label>
            <DateFilterInput
              id="to"
              value={filters.to}
              onChange={(to) => update({ to })}
              min={filters.from ? new Date(filters.from) : undefined}
            />
          </div>
        </CardContent>
      </Card>

      {isError ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Could not load transactions
              {error instanceof Error ? `: ${error.message}` : '.'}
            </p>
          </CardContent>
        </Card>
      ) : isMobile ? (
        // Cards are already separate surfaces, so they stack on the page rather
        // than nesting inside the Card the table uses. The summary keeps its
        // own Card so the net and the pager stay grouped and framed.
        <>
          <MobileSortControl
            options={MOBILE_SORT_OPTIONS}
            value={sorting[0] ? { id: sorting[0].id, desc: sorting[0].desc } : undefined}
            onChange={(next) => {
              onSortingChange([{ id: next.id, desc: next.desc }]);
            }}
          />
          {isLoading ? (
            <div className="space-y-3">
              {['a', 'b', 'c'].map((key) => (
                <Skeleton key={key} className="h-32 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <DataCardList<AdminTransaction>
              data={items}
              getRowId={(row) => `${row.source}-${row.id}`}
              cardConfig={cardConfig}
              emptyMessage="No transactions match these filters."
            />
          )}
          {summary ? <Card>{summary}</Card> : null}
        </>
      ) : (
        <Card>
          <DataTable<AdminTransaction, unknown>
            columns={columns}
            data={isLoading ? [] : items}
            getRowId={(row) => `${row.source}-${row.id}`}
            sorting={sorting}
            onSortingChange={onSortingChange}
            // Loading and empty share the one no-rows slot, which keeps the
            // column headers visible in both states exactly as before. DataTable
            // has no loading mode of its own, and branching away from it here
            // would drop the headers while the first page is in flight.
            emptyMessage={
              isLoading ? (
                <div className="space-y-3 py-1">
                  {['a', 'b', 'c', 'd', 'e'].map((key) => (
                    <Skeleton key={key} className="h-5 w-full" />
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No transactions match these filters.
                </div>
              )
            }
            unframed
          />

          {summary}
        </Card>
      )}
    </div>
  );
}
